/**
 * GTM dataLayer helpers for advertorial (brand advert) tracking.
 *
 * Events pushed here are picked up by GTM Custom Event triggers and forwarded to GA4.
 * See gtm/advertorial-ga4-setup.json for the container configuration checklist.
 *
 * GA4: set VUE_APP_GA4_MEASUREMENT_ID (G-XXXXXXXX) in the env for the build.
 * The legacy UA-119670942-1 ID in public/index.html is sunset and does not receive these events.
 */

const { VUE_APP_GA4_MEASUREMENT_ID, NODE_ENV } = process.env;
const debug = NODE_ENV !== 'production';

function ensureDataLayer() {
	window.dataLayer = window.dataLayer || [];
	return window.dataLayer;
}

/**
 * Load gtag + configure GA4 when a measurement ID is provided at build time.
 * Safe to call alongside existing GTM containers.
 */
function initGa4() {
	const measurementId = VUE_APP_GA4_MEASUREMENT_ID;
	if (!measurementId) {
		debug && console.warn('GTM', 'VUE_APP_GA4_MEASUREMENT_ID is not set — rely on GTM to send events to GA4');
		return;
	}
	if (typeof window === 'undefined') return;

	ensureDataLayer();
	if (!window.gtag) {
		window.gtag = function gtag() {
			window.dataLayer.push(arguments);
		};
	}

	const existing = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
	if (!existing) {
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
		document.head.appendChild(script);
	}

	window.gtag('js', new Date());
	window.gtag('config', measurementId);
	debug && console.log('GTM', 'GA4 configured', measurementId);
}

/**
 * @param {string} eventName
 * @param {Record<string, string|number|undefined>} params
 */
function pushEvent(eventName, params = {}) {
	const payload = { event: eventName, ...params };
	ensureDataLayer().push(payload);
	debug && console.log('GTM', 'dataLayer.push', payload);
}

/**
 * @param {'left'|'right'} side
 * @param {{ tenantId?: string, brandName?: string, adUrl?: string, pageRoute?: string }} meta
 */
function trackAdvertorialImpression(side, meta = {}) {
	pushEvent('advertorial_impression', {
		advertorial_side: side,
		tenant_id: meta.tenantId || '',
		brand_name: meta.brandName || '',
		ad_url: meta.adUrl || '',
		page_route: meta.pageRoute || '',
	});
}

/**
 * @param {'left'|'right'} side
 * @param {{ tenantId?: string, brandName?: string, adUrl?: string, pageRoute?: string }} meta
 */
function trackAdvertorialClick(side, meta = {}) {
	pushEvent('advertorial_click', {
		advertorial_side: side,
		tenant_id: meta.tenantId || '',
		brand_name: meta.brandName || '',
		ad_url: meta.adUrl || '',
		page_route: meta.pageRoute || '',
	});
}

export const Gtm = {
	initGa4,
	pushEvent,
	trackAdvertorialImpression,
	trackAdvertorialClick,
};
